/**
 * Client-side document/image compression for Add Company uploads.
 * Images are resized/JPEG-encoded; oversized PDFs are rasterized into a smaller PDF.
 */

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
const PDF_TYPE = 'application/pdf';

/** Backend per-file limit (FastAPI MAX_UPLOAD_SIZE). */
export const BACKEND_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
/** Reject only huge originals that would freeze the browser. */
export const SOURCE_MAX_BYTES = 30 * 1024 * 1024;

export function isImageFile(file: File): boolean {
  if (IMAGE_TYPES.includes(file.type)) return true;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name);
}

export function isPdfFile(file: File): boolean {
  return file.type === PDF_TYPE || /\.pdf$/i.test(file.name);
}

export function formatFileKb(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

export interface CompressOptions {
  maxDimension?: number;
  maxSizeBytes?: number;
  quality?: number;
}

const DEFAULT_OPTIONS: Required<CompressOptions> = {
  maxDimension: 1600,
  maxSizeBytes: BACKEND_MAX_UPLOAD_BYTES - 64 * 1024,
  quality: 0.82,
};

function scaleToMax(width: number, height: number, maxDimension: number): { w: number; h: number } {
  let w = width;
  let h = height;
  if (w > maxDimension || h > maxDimension) {
    if (w > h) {
      h = Math.round((h * maxDimension) / w);
      w = maxDimension;
    } else {
      w = Math.round((w * maxDimension) / h);
      h = maxDimension;
    }
  }
  return { w: Math.max(1, w), h: Math.max(1, h) };
}

/**
 * Compress an image file until it is at or under maxSizeBytes.
 * Returns original file if not an image or if compression fails.
 */
export function compressImageFile(
  file: File,
  options: CompressOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  if (!isImageFile(file)) {
    return Promise.resolve(file);
  }
  if (file.size <= opts.maxSizeBytes && file.size <= BACKEND_MAX_UPLOAD_BYTES) {
    return Promise.resolve(file);
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      const start = scaleToMax(img.width, img.height, opts.maxDimension);
      let w = start.w;
      let h = start.h;
      let quality = opts.quality;
      const baseName = file.name.replace(/\.[^.]+$/, '');

      const encode = (): void => {
        canvas.width = w;
        canvas.height = h;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const smallEnough = blob.size <= opts.maxSizeBytes;
            if (smallEnough || (quality <= 0.32 && w <= 480)) {
              const out = new File([blob], `${baseName}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(out.size < file.size || smallEnough ? out : file);
              return;
            }
            if (quality > 0.32) {
              quality = Math.max(0.32, quality - 0.12);
            } else {
              w = Math.max(480, Math.round(w * 0.75));
              h = Math.max(480, Math.round(h * 0.75));
              quality = 0.72;
            }
            encode();
          },
          'image/jpeg',
          quality
        );
      };
      encode();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

declare global {
  interface Window {
    pdfjsLib?: {
      getDocument: (opts: { data: ArrayBuffer }) => {
        promise: Promise<{ numPages: number; getPage: (n: number) => Promise<any> }>;
      };
      GlobalWorkerOptions: { workerSrc: string };
    };
  }
}

function loadPdfJs(): Promise<NonNullable<Window['pdfjsLib']>> {
  const src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  const worker = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  return new Promise((resolve, reject) => {
    const finish = () => {
      const lib = window.pdfjsLib;
      if (!lib?.getDocument) {
        reject(new Error('PDF compressor did not load'));
        return;
      }
      lib.GlobalWorkerOptions.workerSrc = worker;
      resolve(lib);
    };

    if (window.pdfjsLib?.getDocument) {
      finish();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-stockm-pdfjs]');
    if (existing) {
      existing.addEventListener('load', finish);
      existing.addEventListener('error', () =>
        reject(new Error('Could not load PDF compression library'))
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.stockmPdfjs = '1';
    script.onload = finish;
    script.onerror = () =>
      reject(new Error('Could not load PDF compression library. Check your network, or upload a photo of the license.'));
    document.head.appendChild(script);
  });
}

async function compressPdfToFit(file: File, maxSizeBytes: number): Promise<File> {
  const pdfjs = await loadPdfJs();
  const { jsPDF } = await import('jspdf');
  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pageCount = Math.min(pdf.numPages || 1, 10);

  let scale = 1.35;
  let jpegQuality = 0.72;
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'license';

  const renderAttempt = async (): Promise<File> => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i += 1) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not compress this PDF in the browser');
      await page.render({ canvasContext: ctx, viewport }).promise;
      const img = canvas.toDataURL('image/jpeg', jpegQuality);
      if (i > 1) doc.addPage();
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
      const drawW = canvas.width * ratio;
      const drawH = canvas.height * ratio;
      const x = (pageW - drawW) / 2;
      const y = (pageH - drawH) / 2;
      doc.addImage(img, 'JPEG', x, y, drawW, drawH);
    }

    const blob = doc.output('blob');
    return new File([blob], `${baseName}.pdf`, {
      type: 'application/pdf',
      lastModified: Date.now(),
    });
  };

  let out = await renderAttempt();
  let rounds = 0;
  while (out.size > maxSizeBytes && rounds < 8) {
    rounds += 1;
    if (jpegQuality > 0.38) {
      jpegQuality = Math.max(0.38, jpegQuality - 0.12);
    } else {
      scale = Math.max(0.45, scale * 0.72);
      jpegQuality = 0.62;
    }
    out = await renderAttempt();
  }
  return out;
}

export const DOCUMENT_COMPRESS_OPTIONS: CompressOptions = {
  maxDimension: 1600,
  maxSizeBytes: BACKEND_MAX_UPLOAD_BYTES - 64 * 1024,
  quality: 0.82,
};

export const LOGO_COMPRESS_OPTIONS: CompressOptions = {
  maxDimension: 800,
  maxSizeBytes: 400 * 1024,
  quality: 0.85,
};

/**
 * Compress a license/CID/ownership/logo file so it fits the 2MB API limit.
 * Large images and PDFs are accepted and reduced automatically.
 */
export async function prepareDocumentForUpload(
  file: File,
  options: CompressOptions = DOCUMENT_COMPRESS_OPTIONS
): Promise<File> {
  if (file.size > SOURCE_MAX_BYTES) {
    throw new Error(`File is too large to process (${formatFileKb(file.size)}). Use a file under 30MB.`);
  }

  const opts = { ...DOCUMENT_COMPRESS_OPTIONS, ...options };
  let out = file;

  if (isImageFile(file)) {
    out = await compressImageFile(file, opts);
  } else if (isPdfFile(file) && file.size > BACKEND_MAX_UPLOAD_BYTES) {
    out = await compressPdfToFit(file, opts.maxSizeBytes || DOCUMENT_COMPRESS_OPTIONS.maxSizeBytes!);
  }

  if (out.size > BACKEND_MAX_UPLOAD_BYTES) {
    throw new Error(
      `Could not compress this file under 2MB (still ${formatFileKb(out.size)}). Try a clearer photo of the page instead of a huge scan.`
    );
  }
  return out;
}
